/**
 * 班级管理 store 模块
 */
import {
  getClasses,
  getClass,
  createClass as apiCreate,
  updateClass as apiUpdate,
  deleteClass as apiDelete
} from '@/api/classes'

/** 规范化班级名称：一(1)班 -> 高一(1)班，若已是正确格式则保留 */
function formatClassName(name) {
  if (!name || typeof name !== 'string') return name || ''
  const trimmed = name.trim()
  const m = trimmed.match(/^([一二三])\((\d+)\)班$/)
  if (m) {
    const map = { 一: '高一', 二: '高二', 三: '高三' }
    return (map[m[1]] || m[1]) + `(${m[2]})班`
  }
  return trimmed
}

function normalizeBindingText(value) {
  const text = String(value ?? '').trim()
  if (!text) return ''
  if (['未绑定', '鏈粦瀹?', '--', '—', 'null', 'undefined'].includes(text)) return ''
  return text
}

/**
 * 将 API 返回项转为前端格式
 * 后端：device_id=班牌编号, machine_code=机器码
 */
function normalizeClass(raw) {
  if (!raw) return null
  const name = raw.class_name ?? raw.name ?? ''
  const normalizedMachineCode = normalizeBindingText(raw.machine_code ?? raw.machineCode ?? '')
  const normalizedDeviceId = normalizeBindingText(raw.device_id ?? raw.deviceId ?? raw.board_id ?? raw.boardId ?? '')
  return {
    id: raw.class_id ?? raw.id,
    name: formatClassName(name),
    grade: raw.grade ?? raw.grade_name ?? raw.gradeName ?? '',
    gradeId: raw.grade_id ?? raw.gradeId ?? raw.grade_segment_id ?? raw.gradeSegmentId ?? '',
    teacher: raw.head_teacher ?? raw.head_teacher_name ?? raw.teacher ?? '',
    studentCount: raw.student_count ?? raw.studentCount ?? 0,
    machineCode: normalizedMachineCode,
    boardId: normalizedDeviceId,
    deviceId: normalizedDeviceId,
    filePath: raw.file_path ?? raw.filePath ?? '',
    headTeacherId: raw.head_teacher_id ?? raw.headTeacherId ?? null
  }
}

function normalizeClassStatistics(res = {}, pagination = {}, list = []) {
  const statistics = res.statistics || {}
  const classTotalCount = Number(
    statistics.class_total_count ??
    statistics.classTotalCount ??
    res.class_total_count ??
    res.classTotalCount ??
    pagination.total_count ??
    pagination.totalCount ??
    res.count ??
    list.length ??
    0
  ) || 0
  const studentTotalCount = Number(
    statistics.student_total_count ??
    statistics.studentTotalCount ??
    res.student_total_count ??
    res.studentTotalCount ??
    list.reduce((sum, item) => sum + Number(item?.studentCount ?? item?.student_count ?? 0), 0)
  ) || 0

  return {
    class_total_count: classTotalCount,
    student_total_count: studentTotalCount
  }
}

/** 转为 API 请求格式 */
function toApiPayload(item) {
  const rawName = (item.name ?? item.class_name ?? '').toString().trim()
  const payload = {
    class_name: formatClassName(rawName) || rawName,
    grade: (item.grade ?? '').toString().trim(),
    device_id: normalizeBindingText(item.deviceId ?? item.boardId ?? item.device_id ?? ''),
    file_path: (item.filePath ?? item.file_path ?? '').toString()
  }
  if (item.headTeacherId != null && item.headTeacherId !== '') {
    const hid = Number(item.headTeacherId)
    payload.head_teacher_id = !isNaN(hid) ? hid : item.headTeacherId
  }
  return payload
}

export default {
  namespaced: true,
  state: {
    list: [],
    pagination: {
      current_page: 1,
      page_size: 10,
      total_count: 0,
      total_pages: 1
    },
    statistics: {
      class_total_count: 0,
      student_total_count: 0
    },
    filters: {},
    loading: false,
    error: null
  },
  getters: {
    classes: state => state.list,
    pagination: state => state.pagination,
    statistics: state => state.statistics,
    filters: state => state.filters,
    loading: state => state.loading,
    error: state => state.error
  },
  mutations: {
    SET_LIST(state, { list, pagination, statistics, filters }) {
      state.list = (list || []).map(normalizeClass).filter(Boolean)
      if (pagination) {
        state.pagination = {
          current_page: pagination.current_page ?? pagination.currentPage ?? 1,
          page_size: pagination.page_size ?? pagination.pageSize ?? 10,
          total_count: pagination.total_count ?? pagination.totalCount ?? pagination.total ?? 0,
          total_pages: pagination.total_pages ?? pagination.totalPages ?? 1
        }
      }
      state.statistics = {
        class_total_count: statistics?.class_total_count ?? statistics?.classTotalCount ?? state.pagination.total_count ?? state.list.length,
        student_total_count: statistics?.student_total_count ?? statistics?.studentTotalCount ?? state.list.reduce((sum, item) => sum + Number(item.studentCount || 0), 0)
      }
      state.filters = filters || {}
    },
    SET_LOADING(state, v) {
      state.loading = !!v
    },
    SET_ERROR(state, err) {
      state.error = err
    }
  },
  actions: {
    /** 获取班级列表（支持分页、筛选） */
    async fetchList({ commit }, params = {}) {
      commit('SET_LOADING', true)
      commit('SET_ERROR', null)
      try {
        const res = await getClasses(params)
        const list = res.data ?? res.list ?? (Array.isArray(res) ? res : [])
        const pag = res.pagination || {}
        const pageSize = params.page_size ?? 10
        const statistics = normalizeClassStatistics(res, pag, list)
        const total = pag.total_count ?? pag.totalCount ?? statistics.class_total_count ?? res.count ?? list.length
        const pagination = {
          current_page: pag.current_page ?? pag.currentPage ?? 1,
          page_size: pag.page_size ?? pag.pageSize ?? pageSize,
          total_count: total,
          total_pages: pag.total_pages ?? pag.totalPages ?? Math.max(1, Math.ceil(total / pageSize))
        }
        const normalized = (list || []).map(normalizeClass).filter(Boolean)
        commit('SET_LIST', { list: normalized, pagination, statistics, filters: res.filters || {} })
        commit('SET_CLASSES', normalized, { root: true })
        return { list: normalized, pagination, statistics, filters: res.filters || {} }
      } catch (e) {
        commit('SET_ERROR', e.message)
        throw e
      } finally {
        commit('SET_LOADING', false)
      }
    },
    /** 拉取全部班级列表，自动翻页合并 */
    async fetchAll({ commit }, params = {}) {
      commit('SET_LOADING', true)
      commit('SET_ERROR', null)
      try {
        const pageSize = Number(params.page_size ?? params.pageSize ?? 500) || 500
        let currentPage = Number(params.page ?? params.current_page ?? 1) || 1
        let totalPages = 1
        let totalCount = 0
        let studentTotalCount = 0
        const merged = []

        do {
          const res = await getClasses({
            ...params,
            page: currentPage,
            page_size: pageSize
          })
          const list = res.data ?? res.list ?? (Array.isArray(res) ? res : [])
          const pag = res.pagination || {}
          const statistics = normalizeClassStatistics(res, pag, list)
          const normalized = (list || []).map(normalizeClass).filter(Boolean)

          merged.push(...normalized)
          totalCount = statistics.class_total_count ?? res.count ?? pag.total_count ?? pag.totalCount ?? totalCount ?? merged.length
          studentTotalCount = statistics.student_total_count ?? studentTotalCount
          totalPages = Number(
            pag.total_pages
            ?? pag.totalPages
            ?? Math.max(1, Math.ceil((totalCount || merged.length) / pageSize))
          ) || 1
          currentPage += 1
        } while (currentPage <= totalPages)

        const uniqueList = Array.from(
          merged.reduce((map, item) => {
            map.set(String(item.id), item)
            return map
          }, new Map()).values()
        )

        const pagination = {
          current_page: 1,
          page_size: pageSize,
          total_count: totalCount || uniqueList.length,
          total_pages: totalPages
        }

        const statistics = {
          class_total_count: totalCount || uniqueList.length,
          student_total_count: studentTotalCount || uniqueList.reduce((sum, item) => sum + Number(item.studentCount || 0), 0)
        }

        commit('SET_LIST', { list: uniqueList, pagination, statistics })
        commit('SET_CLASSES', uniqueList, { root: true })
        return { list: uniqueList, pagination, statistics }
      } catch (e) {
        commit('SET_ERROR', e.message)
        throw e
      } finally {
        commit('SET_LOADING', false)
      }
    },
    /** 获取指定班级 */
    async fetchOne(_, id) {
      const res = await getClass(id)
      const data = res.data ?? res
      return normalizeClass(data)
    },
    /** 创建班级 */
    async create({ rootState }, payload) {
      const data = toApiPayload(payload)
      data.create_by = rootState.currentUser?.name ?? rootState.currentUser?.username ?? 'admin'
      if (process.env.NODE_ENV === 'development') {
        console.log('[班级创建] 请求体:', JSON.stringify(data, null, 2))
      }
      await apiCreate(data)
    },
    /** 更新班级 */
    async update({ rootState }, { id, data }) {
      const payload = toApiPayload({ ...data, id })
      payload.update_by = rootState.currentUser?.name ?? rootState.currentUser?.username ?? 'admin'
      await apiUpdate(id, payload)
    },
    /** 删除班级 */
    async remove(_, id) {
      await apiDelete(id)
    }
  }
}
